import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IonActionSheet, IonIcon, useIonToast } from "@ionic/react";
import {
  arrowRedoOutline,
  arrowUndoOutline,
  cameraOutline,
  closeOutline,
  imagesOutline,
  videocamOutline,
  filmOutline,
} from "ionicons/icons";
import { useDispatch, useSelector } from "react-redux";
import { useMediaPicker } from "../../hooks/useMediaPicker";
import { readVideoDuration } from "../../services/functions";
import { useHardwareBack } from "../../hooks/useHardwareBack";
import StoryTemplate from "./StoryTemplate";
import {
  fetchStoryTray,
  publishStory,
  uploadStoryMedia,
} from "../../redux/actions/storiesActions";
import {
  STORY_RATIO,
  coverGeometry,
  renderStoryFrame,
  rotateImage90,
} from "./storyCanvas";
import "./Stories.css";

// 60, not 100: a Story caption is a line over a picture, not the body of a
// post. The API still accepts up to 200, so this is the binding limit.
const MAX_CAPTION = 60;

/** 1x1 transparent GIF — suppresses the WebView's own oversized play placeholder. */
const BLANK_POSTER =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** DS palette plus white and black, as the spec limits it. */
const TEXT_COLORS = ["#FFFFFF", "#111111", "#65b165", "#ffa500", "#2F80C8"];

const TEXT_BACKGROUNDS = [
  { key: "none", label: "Nessuno" },
  { key: "black", label: "Nero" },
  { key: "white", label: "Bianco" },
  { key: "green", label: "Verde" },
];

const TEXT_POSITIONS = [
  { key: "top", label: "Alto" },
  { key: "center", label: "Centro" },
  { key: "bottom", label: "Basso" },
];

const BACKGROUND_CSS = {
  none: "transparent",
  black: "rgba(0, 0, 0, 0.55)",
  white: "rgba(255, 255, 255, 0.92)",
  green: "var(--ion-color-primary)",
};

const TOOLS = ["Testo", "Colore", "Sfondo", "Posizione"];

/**
 * Manual Story creation: pick one photo, straighten it, frame it to 9:16, add a
 * line of text, publish.
 *
 * The order is deliberate — rotation applies to the source before framing,
 * because a quarter turn after framing would throw away the composition the
 * member just chose.
 *
 * Only the photo is baked into the uploaded file. The caption and its styling
 * travel as fields, so the viewer lays them out and they stay editable.
 */
export default function StoryComposer({
  onClose,
  onPublished,
  prefill = null,
  isOfficial = false,
}) {
  // An automatic Story arrives already composed: cover, real values and a
  // suggested caption. The member edits the words, not the picture, so the
  // picker and the framing step are skipped and the preview renders the same
  // template the viewer will.
  const isAutomatic = Boolean(prefill);
  const dispatch = useDispatch();
  const [presentToast] = useIonToast();
  // Served by the tray endpoint, which reads it from the backend .env — so the
  // limit can change without an app release. Falls back to the same 60 the
  // server defaults to.
  const maxVideoSeconds = useSelector(
    (state) => state.stories?.tray?.maxVideoSeconds ?? 60,
  );
  const maxMediaMb = useSelector((state) => state.stories?.tray?.maxMediaMb ?? 120);

  const [sourceSheetOpen, setSourceSheetOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  // A Storia is a photo or a clip. A clip is not framed — it is letterboxed
  // into the 9:16 frame and uploaded as it was recorded — so it keeps its own
  // state rather than pretending to be an image with a different extension.
  const [video, setVideo] = useState(null); // { file, src, duration }
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [offset, setOffset] = useState({ x: 0.5, y: 0.5 });
  const [caption, setCaption] = useState(prefill?.caption ?? "");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [textBackground, setTextBackground] = useState("black");
  const [textPosition, setTextPosition] = useState("bottom");
  const [activeTool, setActiveTool] = useState("Testo");
  const [isPublishing, setIsPublishing] = useState(false);

  // Set the moment a source is tapped. The action sheet dismisses immediately
  // on tap — long before the camera or gallery returns — so without this the
  // dismiss handler below saw "nothing picked yet" and closed the whole
  // composer. The picker then resolved into an unmounted component and the
  // photo or clip vanished: every source, both platforms, silently.
  const pickInFlightRef = useRef(false);
  const previewVideoRef = useRef(null);
  const frameRef = useRef(null);
  const captionRef = useRef(null);
  const dragRef = useRef(null);

  const onPicked = useCallback(
    async (file) => {
      if ((file?.type || "").startsWith("video/")) {
        let duration;
        try {
          duration = await readVideoDuration(file);
        } catch {
          presentToast({
            message: "Formato video non supportato.",
            duration: 2600,
            color: "danger",
          });
          return;
        }

        // Checked before the duration because it is the harder limit: a phone
        // recording at 4K makes a minute's clip several times this size, and
        // the upload would otherwise fail with a bare "error" after the member
        // had waited for the whole thing to transfer.
        if (file.size > maxMediaMb * 1024 * 1024) {
          presentToast({
            message: `Il video è troppo grande. Puoi caricarne uno fino a ${maxMediaMb} MB.`,
            duration: 3200,
            color: "danger",
          });
          return;
        }

        // Half a second of slack: a clip recorded against a 60 s cap routinely
        // measures 60.02, and refusing that would look broken.
        //
        // A duration we could not read at all is NOT a reason to refuse the
        // clip — the server checks the length too, and the size ceiling above
        // already bounds what can be uploaded. Refusing here would block a
        // perfectly good recording because its container was awkward to probe.
        if (Number.isFinite(duration) && duration > maxVideoSeconds + 0.5) {
          presentToast({
            message: `Il video può durare al massimo ${maxVideoSeconds} secondi.`,
            duration: 2800,
            color: "danger",
          });
          return;
        }

        setImageSrc(null);
        setVideo({ file, src: URL.createObjectURL(file), duration });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setVideo(null);
        setImageSrc(String(reader.result));
        setOffset({ x: 0.5, y: 0.5 });
      };
      reader.readAsDataURL(file);
    },
    [maxVideoSeconds, maxMediaMb, presentToast],
  );

  const picker = useMediaPicker({
    onPicked,
    // Without this the hook swallows a failed read and the composer simply sits
    // there — indistinguishable from the member cancelling. A recording that
    // cannot be read is worth saying out loud.
    onError: () =>
      presentToast({
        message: "Non è stato possibile leggere il file. Riprova.",
        duration: 2800,
        color: "danger",
      }),
    filename: "story.jpg",
    videoBaseName: "story",
    // The web fallback is a file input, so it has to offer both kinds.
    accept: "image/*,video/*",
  });

  // `autoPlay` alone does not start a clip in the Android WebView, and a video
  // that never starts shows the platform's placeholder instead of the picture.
  useEffect(() => {
    const element = previewVideoRef.current;
    if (!element || !video?.src) return;
    element.play().catch(() => {});
  }, [video?.src]);

  // Release the object URL behind the preview when the clip is replaced or the
  // composer closes; without this every retake leaks the previous file.
  useEffect(
    () => () => {
      if (video?.src) URL.revokeObjectURL(video.src);
    },
    [video],
  );

  // Open the source chooser as soon as the composer mounts: the member already
  // said what they wanted by tapping "Condividi una storia". An automatic Story
  // already has its picture and skips this entirely.
  useEffect(() => {
    if (isAutomatic) return;
    if (picker.isNative) setSourceSheetOpen(true);
    else picker.openWebPicker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Measure the picked photo so the drag knows which way it can move.
  useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.onload = () => setNatural({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = imageSrc;
  }, [imageSrc]);

  const slack = useMemo(() => {
    if (!natural.width || !natural.height) return { slackX: 0, slackY: 0 };
    return coverGeometry(natural.width, natural.height);
  }, [natural]);

  const rotate = async (direction) => {
    if (!imageSrc) return;
    setImageSrc(await rotateImage90(imageSrc, direction));
    setOffset({ x: 0.5, y: 0.5 });
  };

  // ── Drag to reposition ─────────────────────────────────────────────────
  const onPointerDown = (event) => {
    if (!frameRef.current) return;
    const rect = frameRef.current.getBoundingClientRect();
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startOffset: { ...offset },
      rect,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;

    // Move by the fraction of the frame travelled, inverted: dragging the photo
    // down reveals more of its top.
    const dx = (event.clientX - drag.startX) / drag.rect.width;
    const dy = (event.clientY - drag.startY) / drag.rect.height;
    const clamp = (value) => Math.min(1, Math.max(0, value));

    setOffset({
      x: slack.slackX > 0 ? clamp(drag.startOffset.x - dx) : 0.5,
      y: slack.slackY > 0 ? clamp(drag.startOffset.y - dy) : 0.5,
    });
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  // Back closes the composer instead of navigating away mid-compose.
  useHardwareBack(true, onClose);

  const publish = async () => {
    if ((!imageSrc && !video && !isAutomatic) || isPublishing) return;
    setIsPublishing(true);

    try {
      const payload = isAutomatic
        ? {
            type: prefill.type,
            source_type: prefill.source_type ?? null,
            source_id: prefill.source_id ?? null,
            background_url: prefill.background_url ?? null,
            template: prefill.template ?? null,
            cta_label: prefill.cta_label ?? null,
            cta_route: prefill.cta_route ?? null,
          }
        : {
            type: "manual",
            // A photo is baked into a 9:16 frame first, so what the member
            // composed is what everyone sees. A clip goes up as recorded — it
            // is letterboxed at render time instead, because re-encoding video
            // in the WebView would cost far more than it is worth for something
            // that lives 24 hours.
            media_path: video
              ? await uploadStoryMedia(video.file)
              : await uploadStoryMedia(await renderStoryFrame(imageSrc, offset)),
            // The server repeats this check; it cannot read the duration off a
            // file already on S3, so the app reports what it measured.
            media_duration:
              video && Number.isFinite(video.duration)
                ? Math.round(video.duration * 100) / 100
                : undefined,
            // Set by the Area Team screen. The backend also refuses to pay
            // coins for one, since an official Story is work, not a milestone.
            is_official: isOfficial || undefined,
          };

      const result = await dispatch(
        publishStory({
          ...payload,
          caption: caption.trim() || null,
          text_color: textColor,
          text_background: textBackground,
          text_position: textPosition,
        }),
      ).unwrap();

      const coins = result?.coin_transaction?.points;
      presentToast({
        message: coins
          ? `Storia pubblicata · +${coins} Monete DS`
          : "Storia pubblicata",
        duration: 2200,
        position: "top",
      });

      dispatch(fetchStoryTray());
      onPublished?.(result);
      onClose();
    } catch (error) {
      presentToast({
        message:
          error?.message ||
          "Non è stato possibile pubblicare la storia. Riprova.",
        duration: 2600,
        color: "danger",
      });
      setIsPublishing(false);
    }
  };

  const captionStyle = {
    background: BACKGROUND_CSS[textBackground],
    color: textColor,
    padding: textBackground === "none" ? 0 : "10px 14px",
    borderRadius: 12,
    textShadow: textBackground === "none" ? "0 1px 6px rgba(0,0,0,0.6)" : "none",
  };

  const overlay = (
    <div className="story-composer">
      <div className="story-composer__bar">
        <button
          type="button"
          className="story-viewer__icon-btn"
          onClick={onClose}
          aria-label="Chiudi"
        >
          <IonIcon icon={closeOutline} aria-hidden="true" />
        </button>

        <span className="story-composer__title">
          {isAutomatic ? "La tua storia" : "Crea una storia"}
        </span>

        {imageSrc && !video && !isAutomatic ? (
          <span className="story-composer__rotate">
            <button
              type="button"
              className="story-viewer__icon-btn"
              onClick={() => rotate("left")}
              aria-label="Ruota a sinistra"
            >
              <IonIcon icon={arrowUndoOutline} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="story-viewer__icon-btn"
              onClick={() => rotate("right")}
              aria-label="Ruota a destra"
            >
              <IonIcon icon={arrowRedoOutline} aria-hidden="true" />
            </button>
          </span>
        ) : (
          <span className="story-viewer__header-spacer" />
        )}
      </div>

      <div className="story-composer__stage">
        <div
          ref={frameRef}
          className="story-composer__frame"
          style={{ aspectRatio: String(STORY_RATIO) }}
          onPointerDown={imageSrc && !video ? onPointerDown : undefined}
          onPointerMove={imageSrc && !video ? onPointerMove : undefined}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {isAutomatic ? (
            <StoryTemplate
              story={{
                attributes: {
                  // ⚠️ The preview must be told which kind of Storia this is,
                  // or it lays out differently from the published one. A
                  // five-star recipe puts its headline and stars at the TOP,
                  // and StoryTemplate decides that from story_type — without
                  // this the member composes against a bottom-anchored preview
                  // and then sees something else in the tray. (A Sfida happened
                  // to survive because it keys off template.tone, which is
                  // already here.)
                  story_type: prefill.type,
                  background_url: prefill.background_url,
                  template: prefill.template,
                  caption,
                  text_color: textColor,
                  text_background: textBackground,
                  text_position: textPosition,
                },
              }}
            />
          ) : video ? (
            <>
              {/* Letterboxed, not cropped: there is no framing step for a clip,
                  so showing all of it is the honest preview. Muted and looping
                  so the composer stays quiet while the caption is written. */}
              <video
                ref={previewVideoRef}
                src={video.src}
                className="story-composer__video"
                // Same blank poster as the viewer: without one the WebView
                // draws its own grey play placeholder over the whole frame
                // until playback starts, which is what flashed up while a clip
                // was being prepared.
                poster={BLANK_POSTER}
                autoPlay
                muted
                loop
                playsInline
              />
              {caption ? (
                <div
                  className="story-composer__caption-layer"
                  style={{
                    justifyContent:
                      textPosition === "top"
                        ? "flex-start"
                        : textPosition === "center"
                          ? "center"
                          : "flex-end",
                  }}
                >
                  <p className="story-composer__caption" style={captionStyle}>
                    {caption}
                  </p>
                </div>
              ) : null}
            </>
          ) : imageSrc ? (
            <>
              <img
                src={imageSrc}
                alt=""
                className="story-composer__photo"
                style={{
                  objectPosition: `${offset.x * 100}% ${offset.y * 100}%`,
                }}
                draggable={false}
              />
              {caption ? (
                <div
                  className="story-composer__caption-layer"
                  style={{
                    justifyContent:
                      textPosition === "top"
                        ? "flex-start"
                        : textPosition === "center"
                          ? "center"
                          : "flex-end",
                  }}
                >
                  <p className="story-composer__caption" style={captionStyle}>
                    {caption}
                  </p>
                </div>
              ) : null}
            </>
          ) : (
            // Tappable: if the member opens the camera and backs out, the sheet
            // has already dismissed, and without this the composer would sit on
            // an empty frame with no way back to the choices.
            <button
              type="button"
              className="story-composer__empty"
              onClick={() => {
                pickInFlightRef.current = false;
                if (picker.isNative) setSourceSheetOpen(true);
                else picker.openWebPicker();
              }}
            >
              Scegli una foto o un video per iniziare
            </button>
          )}
        </div>

        {!isAutomatic && imageSrc && !video && (slack.slackX > 0 || slack.slackY > 0) ? (
          <p className="story-composer__hint">
            Trascina la foto per scegliere l'inquadratura
          </p>
        ) : null}
      </div>

      {imageSrc || video || isAutomatic ? (
        <div className="story-composer__tools">
          <div className="story-composer__tabs" role="tablist">
            {TOOLS.map((tool) => (
              <button
                key={tool}
                type="button"
                role="tab"
                aria-selected={activeTool === tool}
                className={
                  "story-composer__tab" +
                  (activeTool === tool ? " story-composer__tab--active" : "")
                }
                onClick={() => {
                  setActiveTool(tool);
                  if (tool === "Testo") captionRef.current?.focus();
                }}
              >
                {tool}
              </button>
            ))}
          </div>

          <div className="story-composer__panel">
            {activeTool === "Testo" ? (
              <label className="story-composer__field">
                <textarea
                  ref={captionRef}
                  className="story-composer__input"
                  value={caption}
                  maxLength={MAX_CAPTION}
                  rows={2}
                  placeholder="Scrivi qualcosa..."
                  onChange={(e) => setCaption(e.target.value)}
                />
                <span className="story-composer__count">
                  {caption.length}/{MAX_CAPTION}
                </span>
              </label>
            ) : null}

            {activeTool === "Colore" ? (
              <div className="story-composer__swatches">
                {TEXT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    aria-label={`Colore ${color}`}
                    aria-pressed={textColor === color}
                    className={
                      "story-composer__swatch" +
                      (textColor === color ? " story-composer__swatch--active" : "")
                    }
                    style={{ background: color }}
                    onClick={() => setTextColor(color)}
                  />
                ))}
              </div>
            ) : null}

            {activeTool === "Sfondo" ? (
              <div className="story-composer__chips">
                {TEXT_BACKGROUNDS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={textBackground === option.key}
                    className={
                      "story-composer__chip" +
                      (textBackground === option.key ? " story-composer__chip--active" : "")
                    }
                    onClick={() => setTextBackground(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}

            {activeTool === "Posizione" ? (
              <div className="story-composer__chips">
                {TEXT_POSITIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={textPosition === option.key}
                    className={
                      "story-composer__chip" +
                      (textPosition === option.key ? " story-composer__chip--active" : "")
                    }
                    onClick={() => setTextPosition(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            className="story-composer__publish"
            onClick={publish}
            disabled={isPublishing}
          >
            {isPublishing ? "Pubblicazione..." : "Pubblica storia"}
          </button>
        </div>
      ) : null}

      <input {...picker.webInputProps} />
    </div>
  );

  return (
    <>
      {createPortal(overlay, document.querySelector("ion-app") ?? document.body)}

      <IonActionSheet
        isOpen={sourceSheetOpen}
        onDidDismiss={() => {
          setSourceSheetOpen(false);
          // Backing out WITHOUT choosing a source means backing out of the whole
          // thing, rather than being left on an empty frame. Choosing one and
          // waiting for the camera is not backing out.
          if (!pickInFlightRef.current && !imageSrc && !video) onClose();
        }}
        header="Crea una storia"
        buttons={[
          {
            text: "Scatta una foto",
            icon: cameraOutline,
            // Fire and forget: returning the promise makes Ionic hold the sheet
            // open until the picker resolves, which reads as a frozen app.
            handler: () => {
              pickInFlightRef.current = true;
              picker.pickFromCamera();
            },
          },
          {
            text: "Scegli dalla galleria",
            icon: imagesOutline,
            handler: () => {
              pickInFlightRef.current = true;
              picker.pickFromGallery();
            },
          },
          // Recording has no web implementation in the camera plugin, so the
          // option is simply absent in the browser rather than throwing.
          ...(picker.canRecordVideo
            ? [
                {
                  text: `Registra un video (max ${maxVideoSeconds}s)`,
                  icon: videocamOutline,
                  handler: () => {
              pickInFlightRef.current = true;
              picker.recordVideo();
            },
                },
              ]
            : []),
          {
            text: "Scegli un video",
            icon: filmOutline,
            handler: () => {
              pickInFlightRef.current = true;
              picker.pickVideoFromGallery();
            },
          },
          { text: "Annulla", role: "cancel" },
        ]}
      />
    </>
  );
}
